/**
 * Catalog of dimensions selectable in the Adebify LTV detail 項目切替 modal.
 *
 * Dimensions are partitioned by 集計軸 (priority_axis_media_sync /
 * priority_axis_ebis) and 商品軸 (ad_product / purchased_product). Keys must
 * match adebify/jug-api/src/layers/adebify/query_builder/dimension_registry.py.
 *
 * Source of truth: adebify/docs/要件.md req 5.2 + 付録A-2 + the
 * design-mocks/ltv-analysis.html #displayItemsModal layout.
 */

import { PRIORITY_AXIS_EBIS } from 'domain/consts';
import dedupeDimensions from 'domain/adebify/dedupeDimensions';

// Common dimensions shown in both 媒体項目 and アドエビス項目 modes.
// In ad mode, channel_type is hidden from the modal and excluded from defaults.
const COMMON_AGGREGATION_DIMENSIONS = [
  { key: 'channel_type', label: 'チャネル種別' },
];

// 媒体項目 mode dimensions (per 要件.md L109 + mock #ltvMediaItems).
// Labels switch based on traffic mode (要件14-2 AC4):
//   全トラフィック: カテゴリ, 名称
//   広告: 媒体, 広告名
const MEDIA_AGGREGATION_DIMENSIONS = [
  { key: 'media', label: 'カテゴリ', adLabel: '媒体' },
  { key: 'media_campaign', label: '媒体キャンペーン', adLabel: '媒体キャンペーン' },
  { key: 'media_ad_group', label: '媒体広告グループ', adLabel: '媒体広告グループ' },
  { key: 'media_ad_id', label: '媒体広告ID', adLabel: '媒体広告ID' },
  { key: 'media_ad_name', label: '名称', adLabel: '広告名' },
];

// アドエビス項目 mode dimensions (per 要件.md L109).
const EBIS_AGGREGATION_DIMENSIONS = [
  { key: 'media_type', label: 'カテゴリ', adLabel: '媒体種別' },
  { key: 'ad_group1', label: '広告グループ1', adLabel: '広告グループ1' },
  { key: 'ad_group2', label: '広告グループ2', adLabel: '広告グループ2' },
  { key: 'ad_id', label: '広告ID', adLabel: '広告ID' },
  { key: 'ad_name', label: '名称', adLabel: '広告名' },
  { key: 'remarks', label: '備考', adLabel: '備考' },
  { key: 'posting_period', label: '出稿期間', adLabel: '出稿期間' },
];

// LP / device dimensions are mode-agnostic and appended after the mode-
// specific list (mock places them after 広告名 / 出稿期間).
const SHARED_TAIL_DIMENSIONS = [
  { key: 'device', label: 'デバイス' },
  { key: 'lp_domain', label: 'ランディングページドメイン' },
  { key: 'lp_url', label: 'ランディングページURL' },
];

const getAggregationDimensionsByAxis = (axis) => {
  const mid =
    axis === PRIORITY_AXIS_EBIS
      ? EBIS_AGGREGATION_DIMENSIONS
      : MEDIA_AGGREGATION_DIMENSIONS;
  return [
    ...COMMON_AGGREGATION_DIMENSIONS,
    ...mid,
    ...SHARED_TAIL_DIMENSIONS,
  ];
};

const ALL_AGGREGATION_KEYS = new Set([
  ...COMMON_AGGREGATION_DIMENSIONS.map((d) => d.key),
  ...MEDIA_AGGREGATION_DIMENSIONS.map((d) => d.key),
  ...EBIS_AGGREGATION_DIMENSIONS.map((d) => d.key),
  ...SHARED_TAIL_DIMENSIONS.map((d) => d.key),
]);

// Product-side dimensions. Generic keys used in the UI (`product_category`,
// `product_name`); mapped to API keys based on the selected 商品軸 (mirrors
// product-analysis modal's apiKey mapping).
// 商品軸の選択に応じて表示を切り替える:
//   購入商品軸: 購入商品カテゴリ, 購入商品名, 購入商品バリエーション
//   広告対象商品軸: 広告商品カテゴリ, 広告商品名, 広告商品バリエーション
//
// 広告対象商品の表示名フォールバック:
//   CSVでアップロードされた名前を優先。未指定時は product_id/variant_id をキーに
//   Shopify連携の shop.product_variants テーブルの名前にフォールバックする。
const PRODUCT_DIMENSIONS = [
  { key: 'product_category', label: '商品カテゴリ' },
  { key: 'product_name', label: '商品名' },
  { key: 'product_variant', label: 'バリエーション' },
  // API-resolved product keys (used in actual response data)
  { key: 'purchased_product', label: '購入商品名' },
  { key: 'purchased_variant', label: '購入商品バリエーション' },
  { key: 'ad_product_name', label: '広告商品名' },
  { key: 'ad_product_category', label: '広告商品カテゴリ' },
  { key: 'ad_product_variant', label: '広告商品バリエーション' },
];

const PRODUCT_KEYS = new Set(PRODUCT_DIMENSIONS.map((d) => d.key));

const productKeyToApiKey = (key, productAxis) => {
  if (key === 'product_name') {
    return productAxis === 'ad_product' ? 'ad_product_name' : 'purchased_product';
  }
  if (key === 'product_category') {
    return productAxis === 'ad_product'
      ? 'ad_product_category'
      : 'product_category';
  }
  if (key === 'product_variant') {
    return productAxis === 'ad_product'
      ? 'ad_product_variant'
      : 'purchased_variant';
  }
  return key;
};

const apiKeyToProductKey = (apiKey) => {
  if (apiKey === 'ad_product_name' || apiKey === 'purchased_product') {
    return 'product_name';
  }
  if (apiKey === 'ad_product_category' || apiKey === 'product_category') {
    return 'product_category';
  }
  if (apiKey === 'ad_product_variant' || apiKey === 'purchased_variant') {
    return 'product_variant';
  }
  return apiKey;
};

// Default dimensions for the LTV detail.
// All-traffic mode: channel_type + media are default.
// Ad mode: channel_type is hidden, so only media is the effective default.
const LTV_DETAIL_DEFAULT_DIMENSIONS = [
  'channel_type',
  'media',
];

const PURCHASED_PRODUCT_FALLBACK_DIMENSIONS = ['product_category', 'product_name'];

/**
 * Get the display label for a dimension key based on traffic mode.
 * In ad mode, uses `adLabel` (要件14-2 AC4); in all-traffic mode, uses `label`.
 * @param {string} key - dimension key
 * @param {boolean} isAdMode - true if traffic_mode === 'ad'
 * @returns {string} display label
 */
const getDimensionLabel = (key, isAdMode) => {
  const allDims = [
    ...COMMON_AGGREGATION_DIMENSIONS,
    ...MEDIA_AGGREGATION_DIMENSIONS,
    ...EBIS_AGGREGATION_DIMENSIONS,
    ...SHARED_TAIL_DIMENSIONS,
    ...PRODUCT_DIMENSIONS,
  ];
  const dim = allDims.find((d) => d.key === key);
  if (!dim) return key;
  if (isAdMode && dim.adLabel) return dim.adLabel;
  return dim.label;
};

const sameSet = (a, b) => {
  if (a.length !== b.length) return false;
  const setA = new Set(a);
  return b.every((key) => setA.has(key));
};

const toDimensionArray = (dimensions) => {
  if (!dimensions) return [];
  if (Array.isArray(dimensions)) return dimensions.filter(Boolean);
  return String(dimensions)
    .split(',')
    .map((key) => key.trim())
    .filter(Boolean);
};

const getVisibleDefaultAggregationKeys = (isAdMode) =>
  isAdMode
    ? LTV_DETAIL_DEFAULT_DIMENSIONS.filter((key) => key !== 'channel_type')
    : LTV_DETAIL_DEFAULT_DIMENSIONS;

const isDefaultAggregationSelection = (selectorKeys, isAdMode) => {
  const aggregation = (selectorKeys || []).filter((key) =>
    ALL_AGGREGATION_KEYS.has(key)
  );
  if (aggregation.length === 0) return false;
  return (
    sameSet(aggregation, LTV_DETAIL_DEFAULT_DIMENSIONS) ||
    sameSet(aggregation, getVisibleDefaultAggregationKeys(isAdMode))
  );
};

const splitSelectorDimensions = (selectorKeys) => {
  const aggregation = (selectorKeys || []).filter((key) =>
    ALL_AGGREGATION_KEYS.has(key)
  );
  const product = (selectorKeys || []).filter((key) => PRODUCT_KEYS.has(key));
  return { aggregation, product };
};

/**
 * Stored settings may still carry the untouched default aggregation bundle
 * alongside a product dimension the user added. Treat that as product-only.
 */
const normalizeSelectorKeysFromStore = (selectorKeys, isAdMode) => {
  const { aggregation, product } = splitSelectorDimensions(selectorKeys);
  if (
    product.length > 0 &&
    isDefaultAggregationSelection(aggregation, isAdMode)
  ) {
    return product;
  }
  return selectorKeys;
};

const parseApiDimensionsToSelectorKeys = (
  dimensions,
  isAdMode,
  fallback = LTV_DETAIL_DEFAULT_DIMENSIONS
) => {
  if (!dimensions || !dimensions.length) return [...fallback];
  const selectorKeys = toDimensionArray(dimensions).map(apiKeyToProductKey);
  return normalizeSelectorKeysFromStore(selectorKeys, isAdMode);
};

const buildLtvDetailApiDimensions = (selectorKeys, productAxis) => {
  const { aggregation, product } = splitSelectorDimensions(selectorKeys);
  const ordered = [...aggregation, ...product];
  return dedupeDimensions(
    ordered.map((key) => productKeyToApiKey(key, productAxis))
  );
};

const normalizeLtvDetailApiDimensions = (dimensions, productAxis, isAdMode) => {
  let selectorKeys = parseApiDimensionsToSelectorKeys(dimensions, isAdMode, []);
  // In ad mode, channel_type is not applicable (single channel context)
  if (isAdMode) {
    selectorKeys = selectorKeys.filter((key) => key !== 'channel_type');
  }
  return buildLtvDetailApiDimensions(selectorKeys, productAxis);
};

const applyProductDimensionToggle = (selectorKeys, key, isAdMode, enabled) => {
  if (!enabled) {
    return selectorKeys.filter((item) => item !== key);
  }

  let next = [...selectorKeys, key];
  if (
    PRODUCT_KEYS.has(key) &&
    isDefaultAggregationSelection(selectorKeys, isAdMode)
  ) {
    next = next.filter((item) => !ALL_AGGREGATION_KEYS.has(item));
  }
  return next;
};

const resolveLtvDetailDisplayDimensionKeys = (dimensions, axis, isAdMode) => {
  const selected = new Set(toDimensionArray(dimensions));
  if (isAdMode) {
    selected.delete('channel_type');
  }

  const catalogOrder = [
    ...getAggregationDimensionsByAxis(axis).map((item) => item.key),
    ...PRODUCT_DIMENSIONS.map((item) => item.key),
  ];

  return catalogOrder.filter((key) => selected.has(key));
};

export {
  COMMON_AGGREGATION_DIMENSIONS,
  MEDIA_AGGREGATION_DIMENSIONS,
  EBIS_AGGREGATION_DIMENSIONS,
  SHARED_TAIL_DIMENSIONS,
  PRODUCT_DIMENSIONS,
  ALL_AGGREGATION_KEYS,
  PRODUCT_KEYS,
  LTV_DETAIL_DEFAULT_DIMENSIONS,
  PURCHASED_PRODUCT_FALLBACK_DIMENSIONS,
  getAggregationDimensionsByAxis,
  getDimensionLabel,
  productKeyToApiKey,
  apiKeyToProductKey,
  parseApiDimensionsToSelectorKeys,
  buildLtvDetailApiDimensions,
  normalizeLtvDetailApiDimensions,
  applyProductDimensionToggle,
  resolveLtvDetailDisplayDimensionKeys,
  splitSelectorDimensions,
};
